import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Button, Card, Badge, Tabs, EmptyState } from '../components/design-system';
import { Cpu, Trash2, XCircle, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

const statusTabs = [
  { id: '', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'running', label: 'Running' },
  { id: 'completed', label: 'Completed' },
  { id: 'failed', label: 'Failed' },
];

export const JobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => { loadJobs(); }, [page, statusFilter]);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', '30');
      if (statusFilter) params.set('status', statusFilter);
      const res = await api.getJobs(params.toString());
      setJobs(res.jobs || []);
      setTotal(res.total || 0);
      setTotalPages(res.totalPages || 1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCancel = async (id) => {
    try { await api.cancelJob(id); loadJobs(); }
    catch (err) { alert(err.message); }
  };

  const handleClearCompleted = async () => {
    try { await api.clearCompletedJobs(); loadJobs(); }
    catch (err) { alert(err.message); }
  };

  const statusVariant = { pending: 'default', running: 'info', completed: 'success', failed: 'danger', cancelled: 'default' };

  return (
    <div className="space-y-4 animate-fade-in max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Jobs Queue</h2>
          <p className="text-sm text-gray-500">{total} jobs</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" icon={RefreshCw} onClick={loadJobs}>Refresh</Button>
          <Button variant="secondary" size="sm" icon={Trash2} onClick={handleClearCompleted}>Clear Completed</Button>
        </div>
      </div>

      <Tabs tabs={statusTabs} activeTab={statusFilter} onChange={(id) => { setStatusFilter(id); setPage(1); }} />

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" /></div>
      ) : jobs.length === 0 ? (
        <EmptyState icon={Cpu} title="No jobs" description="Background jobs for fetching, analysis, and generation appear here." />
      ) : (
        <div className="space-y-2">
          {jobs.map(job => (
            <Card key={job.id} className="!p-3">
              <div className="flex items-center gap-4">
                <Badge variant={statusVariant[job.status] || 'default'} dot>{job.status}</Badge>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-gray-900">{job.type.replace(/_/g, ' ')}</span>
                  <span className="text-xs text-gray-400 ml-2">#{job.id}</span>
                  {job.error && <p className="text-xs text-red-500 mt-0.5 truncate">{job.error}</p>}
                </div>
                <div className="text-xs text-gray-400 flex-shrink-0">
                  {job.attempts > 0 && <span>Attempt {job.attempts}/{job.max_attempts} · </span>}
                  {new Date(job.created_at).toLocaleString()}
                </div>
                {(job.status === 'pending' || job.status === 'running') && (
                  <Button variant="ghost" size="sm" icon={XCircle} onClick={() => handleCancel(job.id)}>Cancel</Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" icon={ChevronLeft} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
            <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next<ChevronRight className="h-4 w-4 ml-1" /></Button>
          </div>
        </div>
      )}
    </div>
  );
};
